import axios from 'axios';
import { secureLocalStorage } from '../../utils/crypto';
import { getTenantId, getTenantConfig } from '../tenant/tenantService.js';
import { getAssetsByType } from '../core/assetsService.js';
import { getUserProfile } from './userService.js';
import { hasSegmentMatch } from '../core/segmentHelper.js';

const PARTICIPANTS_URL = process.env.REACT_APP_PARTICIPANTS_URL;

const getApiHeaders = () => ({
  'X-Tenant-Id': getTenantId(),
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${secureLocalStorage.getItem('idToken')}`
});

const api = axios.create({
  baseURL: PARTICIPANTS_URL,
});

const TERMS_ASSET_TYPES = [0, 1, 3];

const _contentCache = new Map();
const _pendingFetches = new Map();

const TEXT_EXTENSIONS = ['html', 'txt'];

/**
 * A API de Termos ainda não tem atributo nativo para restringir por profile, então o
 * mapeamento fica em FEATURE_FLAGS.TERMS_PROFILES (chave livre, repassada sem whitelist por
 * tenantService.js) até que a API de Termos suporte isso nativamente via term.profiles.
 * Formato: { "<idDoTermo>": ["profileValueA", "profileValueB"] }
 */
const getTermsProfilesMap = () => getTenantConfig()?.FEATURE_FLAGS?.TERMS_PROFILES || {};

/**
 * Termo sem profiles configurado → visível para todos.
 * Termo com um ou mais profiles configurados → só para participantes com um desses PROFILE_SELECT.
 * Reaproveita hasSegmentMatch (mesma regra de elegibilidade, com OR entre profiles configurados).
 */
const isTermVisibleForProfile = (term, profile) => {
  const eligibleProfiles = term.profiles?.length ? term.profiles : getTermsProfilesMap()[term.id];
  return hasSegmentMatch(eligibleProfiles, profile ? [profile] : []);
};

const _fetchAndCache = (url) => {
  if (_pendingFetches.has(url)) return _pendingFetches.get(url);

  const promise = fetch(url)
    .then((res) => res.text())
    .then((text) => {
      _contentCache.set(url, text);
      return text;
    })
    .finally(() => {
      _pendingFetches.delete(url);
    });

  _pendingFetches.set(url, promise);
  return promise;
};

export const termsService = {
  preloadTermContent: (url) => {
    if (!url) return;
    const ext = url.split('.').pop().toLowerCase();
    if (!TEXT_EXTENSIONS.includes(ext)) return;
    if (_contentCache.has(url) || _pendingFetches.has(url)) return;
    _fetchAndCache(url).catch(() => {});
  },

  getTermContent: (url) => {
    if (_contentCache.has(url)) return Promise.resolve(_contentCache.get(url));
    return _fetchAndCache(url);
  },

  checkAcceptedTerms: async (assetIds) => {
    try {
      const response = await api.post(`/api/Participants/check-terms`,
        {
          assetIds: assetIds
        },
        {
          headers: getApiHeaders()
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error checking accepted terms:', error);
      throw error;
    }
  },

  getActiveTerms: async (language = 'pt-BR', profile = getUserProfile()) => {
    try {
      // Busca assets do CMS usando o novo serviço unificado
      const activeTerms = await getAssetsByType(TERMS_ASSET_TYPES, {
        language,
        profile,
        isPublic: true,
        requireAuth: false, // Endpoint público não requer auth
      });
      
      // Filtra apenas assets que são realmente termos (validação adicional)
      const validTerms = activeTerms.filter(term => {
        // Se o asset tem a propriedade type, valida
        if (term.type !== undefined) {
          const isValid = TERMS_ASSET_TYPES.includes(term.type);
          if (!isValid) {
            console.warn('Asset filtered out - invalid type:', {
              id: term.id,
              type: term.type,
              validTypes: TERMS_ASSET_TYPES
            });
          }
          return isValid;
        }
        // Se não tem type, assume que é válido (pode ser que o backend não retorne)
        return true;
      });
      
      const profileTerms = validTerms.filter(term => isTermVisibleForProfile(term, profile));

      const assetIds = profileTerms.map(term => term.id);
      if (assetIds.length === 0) {
        return [];
      }

      const acceptedTerms = await termsService.checkAcceptedTerms(assetIds);

      return profileTerms.filter(term => {
        const acceptedTerm = acceptedTerms.find(accepted => accepted.assetId === term.id);
        return !acceptedTerm?.isAccepted;
      });

    } catch (error) {
      console.error('Error fetching active terms:', error);
      throw error;
    }
  },

  getActiveTermsRegister: async (language = 'pt-BR', profile = getUserProfile()) => {
    try {
      // Busca assets do CMS usando o novo serviço unificado
      const activeTerms = await getAssetsByType(TERMS_ASSET_TYPES, {
        language,
        profile,
        isPublic: true,
        requireAuth: false, // Endpoint público não requer auth
      });
      
      // Filtra apenas assets que são realmente termos (validação adicional)
      const validTerms = activeTerms.filter(term => {
        // Se o asset tem a propriedade type, valida
        if (term.type !== undefined) {
          const isValid = TERMS_ASSET_TYPES.includes(term.type);
          if (!isValid) {
            console.warn('Asset filtered out - invalid type:', {
              id: term.id,
              type: term.type,
              validTypes: TERMS_ASSET_TYPES
            });
          }
          return isValid;
        }
        // Se não tem type, assume que é válido (pode ser que o backend não retorne)
        return true;
      });

      return validTerms.filter(term => isTermVisibleForProfile(term, profile));

    } catch (error) {
      console.error('Error fetching active terms:', error);
      throw error;
    }
  },

  getPublicActiveTerms: async (language = 'pt-BR', type = 0) => {
    try {
      // Busca assets públicos do CMS usando o novo serviço unificado
      const activeTerms = await getAssetsByType([type], {
        language,
        isPublic: true,
        requireAuth: false,
      });

      // Área deslogada (cadastro): nunca aplica profile, mesmo que o formulário já
      // tenha um PROFILE_SELECT escolhido — só termos sem restrição aparecem aqui.
      // Termos restritos por profile só aparecem depois do login (getActiveTerms/getActiveTermsRegister).
      return activeTerms.filter(term => isTermVisibleForProfile(term, null));

    } catch (error) {
      console.error('Error fetching public active terms:', error);
      throw error;
    }
  },

  acceptTerm: async (assetId, asset = null) => {
    try {
      // Se o asset não foi passado, tenta buscar para validar o tipo
      let termAsset = asset;
      if (!termAsset && assetId) {
        // Valida se o assetId é válido antes de fazer a requisição
        console.log('Accepting term with assetId:', assetId);
        
        // Se temos o assetId mas não o objeto, precisamos validar
        // Vamos buscar o asset para verificar seu tipo
        try {
          const { getAssetById } = await import('../core/assetsService.js');
          termAsset = await getAssetById(assetId, false);
          
          // Valida se o asset é realmente um termo
          if (termAsset && termAsset.type !== undefined) {
            if (!TERMS_ASSET_TYPES.includes(termAsset.type)) {
              const error = new Error(`Invalid asset type: ${termAsset.type}. Expected one of: ${TERMS_ASSET_TYPES.join(', ')}`);
              error.code = 'INVALID_ASSET_TYPE';
              console.error('Error: Asset is not a valid term type', {
                assetId,
                assetType: termAsset.type,
                validTypes: TERMS_ASSET_TYPES
              });
              throw error;
            }
          } else {
            console.warn('Asset does not have type property, proceeding anyway', { assetId, asset: termAsset });
          }
        } catch (fetchError) {
          // Se não conseguir buscar o asset, continua mesmo assim
          // O backend vai validar
          console.warn('Could not fetch asset for validation, proceeding anyway', fetchError);
        }
      } else if (termAsset) {
        // Valida se o asset passado é realmente um termo
        if (termAsset.type !== undefined && !TERMS_ASSET_TYPES.includes(termAsset.type)) {
          const error = new Error(`Invalid asset type: ${termAsset.type}. Expected one of: ${TERMS_ASSET_TYPES.join(', ')}`);
          error.code = 'INVALID_ASSET_TYPE';
          console.error('Error: Asset is not a valid term type', {
            assetId,
            assetType: termAsset.type,
            validTypes: TERMS_ASSET_TYPES
          });
          throw error;
        }
      }

      const userAgent = navigator.userAgent;
      const browser = userAgent.split(' ').find(part => part.includes('/')) || 'Unknown';
      
      let geoLocation = { latitude: '0', longitude: '0' };
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });
          geoLocation = {
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString()
          };
        } catch (error) {
          console.warn('Geolocation error:', error);
        }
      }

      const response = await api.post(`/api/Participants/accept-terms`,
        {
          assetIds: [assetId],
          info: {
            fingerPrint: secureLocalStorage.getItem('fingerPrint') || 'unknown',
            browser,
            geoLocation
          }
        },
        {
          headers: getApiHeaders()
        }
      );
      return response.data;
    } catch (error) {
      // Melhora o tratamento de erro para mostrar mensagens mais claras
      if (error.response) {
        // Erro da API
        const apiError = error.response.data;
        if (Array.isArray(apiError) && apiError.length > 0) {
          const firstError = apiError[0];
          if (firstError.code === 'Participant.AcceptTermError' && firstError.description === 'Invalid asset type') {
            const enhancedError = new Error(`Tipo de asset inválido. O asset ${assetId} não é um termo válido.`);
            enhancedError.code = 'INVALID_ASSET_TYPE';
            enhancedError.originalError = firstError;
            console.error('Error accepting term - Invalid asset type:', {
              assetId,
              error: firstError,
              asset: asset
            });
            throw enhancedError;
          }
        }
        console.error('Error accepting term (API error):', {
          status: error.response.status,
          data: error.response.data,
          assetId
        });
        throw error;
      } else if (error.code === 'INVALID_ASSET_TYPE') {
        // Erro de validação local
        throw error;
      } else {
        // Outros erros
        console.error('Error accepting term:', error);
        throw error;
      }
    }
  }
}; 
